import React, { useCallback, useEffect, useRef, useState } from "react";
import { MagnifyingGlass, X, CaretDown, Prohibit } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-66 — Server-searchable multi-select of active affiliates.
 *
 * Used by the Destination form to build `blocked_affiliate_ids` — a
 * per-destination blocklist that removes selected affiliates from
 * routing to that specific destination (i.e. "remove affiliate A from
 * campaign B" without pausing the affiliate globally).
 *
 * Contract:
 *   - `value`: string[] of affiliate ids.
 *   - `onChange(nextIds: string[])`.
 *   - `initialAffiliates?`: seed of `{id, name, public_source_id}` used
 *     to render selected-chip labels on Edit-load before a search
 *     resolves the same rows. Missing labels are resolved on-demand
 *     via `GET /organizations/{id}` (mirrors BuyerCombobox).
 *
 * RBAC: relies on `/organizations?type=affiliate` which is already
 * blocked for buyer / vendor / media_buyer roles.
 */
export default function AffiliateMultiSelect({
  value = [],
  onChange,
  initialAffiliates = [],
  placeholder = "Search affiliates to block…",
  testIdPrefix = "aff-multi",
}) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [labelCache, setLabelCache] = useState(() => {
    const m = {};
    for (const a of initialAffiliates || []) {
      if (a && a.id) m[a.id] = a;
    }
    return m;
  });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Merge freshly-fetched rows into the label cache so the chip stays
  // resolved after the dropdown closes.
  const rememberRows = useCallback((list) => {
    setLabelCache((prev) => {
      const next = { ...prev };
      for (const r of list) {
        if (r && r.id) next[r.id] = r;
      }
      return next;
    });
  }, []);

  const runSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: "affiliate",
        page_size: "25",
        sort: "name_asc",
        status: "active",
      });
      const trimmed = (query || "").trim();
      if (trimmed) params.set("q", trimmed);
      const { data } = await api.get(`/organizations?${params.toString()}`);
      const list = data.organizations || [];
      setRows(list);
      rememberRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rememberRows]);

  // Resolve unknown selected ids so chips render a real name.
  useEffect(() => {
    let cancelled = false;
    const unresolved = (value || []).filter((id) => !labelCache[id]);
    if (unresolved.length === 0) return () => {};
    (async () => {
      const results = await Promise.all(unresolved.map(async (id) => {
        try {
          const { data } = await api.get(`/organizations/${id}`);
          return data;
        } catch {
          return { id, name: id };
        }
      }));
      if (!cancelled) rememberRows(results);
    })();
    return () => { cancelled = true; };
    // We intentionally omit labelCache from the deps to prevent an
    // update-loop: resolving a new label mutates labelCache which
    // would refire this effect. `value` alone is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.join("|")]);

  // Debounced search on term change (only when dropdown is open).
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [term, open, runSearch]);

  // Fetch initial page on open.
  useEffect(() => {
    if (open && rows.length === 0 && !term) runSearch("");
  }, [open, rows.length, term, runSearch]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const isSelected = (id) => value.includes(id);
  const toggle = (row) => {
    if (isSelected(row.id)) onChange?.(value.filter((v) => v !== row.id));
    else onChange?.([...value, row.id]);
  };
  const removeOne = (id) => onChange?.(value.filter((v) => v !== id));
  const clearAll = () => onChange?.([]);

  return (
    <div ref={wrapRef} className="relative w-full" data-testid={`${testIdPrefix}-root`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-trigger`}
        className="w-full border border-black px-2 py-1.5 text-sm text-left inline-flex items-center gap-1 hover:bg-zinc-50 min-h-[34px]"
      >
        <span className="flex-1 truncate text-zinc-500">
          {value.length === 0
            ? placeholder
            : `${value.length} affiliate${value.length === 1 ? "" : "s"} blocked`}
        </span>
        {value.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && clearAll()}
            data-testid={`${testIdPrefix}-clear-all`}
            className="ml-1 border border-zinc-400 text-zinc-500 hover:bg-black hover:text-white px-1 py-0.5 leading-none inline-flex items-center"
            title="Clear all"
          >
            <X size={10} />
          </span>
        )}
        <CaretDown size={12} className="text-zinc-500" />
      </button>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testIdPrefix}-chips`}>
          {value.map((id) => {
            const row = labelCache[id];
            const label = row?.name || id;
            const src = row?.public_source_id;
            return (
              <span
                key={id}
                data-testid={`${testIdPrefix}-chip-${id}`}
                className="inline-flex items-center gap-1 bg-rose-50 border border-rose-800 text-rose-900 px-2 py-0.5 text-[11px]"
              >
                <Prohibit size={10} weight="bold" />
                <span className="truncate max-w-[180px]" title={label}>{label}</span>
                {src && <span className="font-mono text-[9px] opacity-70">{src}</span>}
                <button
                  type="button"
                  onClick={() => removeOne(id)}
                  data-testid={`${testIdPrefix}-chip-remove-${id}`}
                  className="ml-0.5 hover:bg-rose-800 hover:text-white leading-none px-0.5"
                  title="Unblock"
                >
                  <X size={9} weight="bold" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 border-2 border-black bg-white z-40 max-h-64 overflow-y-auto brut-shadow-sm"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          <div className="p-2 border-b border-zinc-300 sticky top-0 bg-white">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                autoFocus
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Type an affiliate name…"
                className="w-full border border-black pl-6 pr-2 py-1 text-sm"
                data-testid={`${testIdPrefix}-input`}
              />
            </div>
          </div>
          <div>
            {loading && (
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
                No affiliates match &quot;{term}&quot;
              </div>
            )}
            {rows.map((r) => {
              const sel = isSelected(r.id);
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => toggle(r)}
                  data-testid={`${testIdPrefix}-option-${r.id}`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-100 border-b border-zinc-100 flex items-center gap-2 ${sel ? "bg-rose-50" : ""}`}
                  title={r.name}
                >
                  <span
                    className={`inline-flex items-center justify-center w-4 h-4 border border-black ${sel ? "bg-black text-white" : "bg-white"}`}
                    aria-hidden="true"
                  >
                    {sel ? "✓" : ""}
                  </span>
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.public_source_id && (
                    <span className="font-mono text-[10px] text-zinc-500">{r.public_source_id}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
